import {
  queriesDocumentTemplates,
  queriesEngagements,
  queriesRequirementDefinitions,
  queriesSharedFileRequestConfigurations
} from "@query";
import {
  toWSDate,
  WSAvatar,
  WSElement,
  WSGrid,
  WSList,
  WSLoader,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSPill,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  RequirementDefinition,
  RequirementDefinitionStatus,
  RequirementType,
  SharedFileRequestJobType
} from "@wingspanhq/wingspan-api-sdk";
import React, { useEffect, useState } from "react";
import { useRouteMatch } from "react-router-dom";

import { WSQueries } from "../../../../query/WSQuery";
import { WayfinderWidget } from "../../../../shared/components/WayfinderWidget";
import { queriesBackgroundCheckConfigurations } from "../../../../v3/query/queriesBackgroundCheckConfigurations";
import { queriesBackgroundCheckPackages } from "../../../../v3/query/queriesBackgroundCheckPackages";
import { queriesInsuranceMonitorConfigurations } from "../../../../v3/query/queriesInsuranceMonitorConfigurations";
import { AIValidationWidget } from "../../../Requirements/components/AIValidationWidget";
import { ReportsInfoWidgetContent } from "../../../Requirements/modules/BackgroundChecks/components/ReportsInfoWidget";
import { selectorIsSharedFileNotNoneContext } from "../../../Requirements/selectors/selectorIsSharedFileNotNoneContext";
import { RequirementContextType } from "../../../Requirements/types";
import { ApprovalWidget } from "../../components/ApprovalWidget";
import { RequirementInformation } from "../../components/RequirementInformation";
import { VersionsWidget } from "../../components/VersionsWidget";
import { useRequirementDefinitionDefaultActions } from "../../hooks/useRequirementDefinitionDefaultActions";
import { IDENTITY_VERIFICATION_CHECKS } from "../../modules/IdentityVerification/constants";
import { useGoToRequirementDefinitions } from "../../paths/helpers";

import { EngagementsTable } from "./EngagementsTable";
import { SelectRequirementDefinitionVersion } from "./SelectRequirementDefinitionVersion";
import { RequirementDefinitionHistoryWidget } from "./useRequirementDefinitionHistoryWidget";

export const RouteRequirementDetails: React.FC = () => {
  const match = useRouteMatch<{ requirementId: string }>();
  const queryRequirementDefinition =
    queriesRequirementDefinitions.getRequirementDefinition.useQuery({
      requirementDefinitionId: match.params.requirementId
    });

  return (
    <WSPage>
      <WSQueries queries={{ queryRequirementDefinition }}>
        {({ queryRequirementDefinitionData }) => (
          <RequirementDetails
            requirementDefinition={queryRequirementDefinitionData}
          />
        )}
      </WSQueries>
    </WSPage>
  );
};

const RequirementDetails: React.FC<{
  requirementDefinition: RequirementDefinition;
}> = props => {
  const [requirementDefinition, setRequirementDefinition] =
    useState<RequirementDefinition>(props.requirementDefinition);

  useEffect(() => {
    setRequirementDefinition(props.requirementDefinition);
  }, [props.requirementDefinition]);

  const requirementId = requirementDefinition.id!;
  const goToRequirementDefinitions = useGoToRequirementDefinitions();
  const requirementDefinitionDefaultActions =
    useRequirementDefinitionDefaultActions();
  const queryEngagementsList = queriesEngagements.listEngagements.useQueryAll(
    undefined,
    { refetchOnMount: false, retry: false }
  );

  const signedDocumentTemplateQuery =
    queriesDocumentTemplates.getDocumentTemplate.useQuery(
      { templateId: requirementDefinition.dataSourceId as string },
      {
        enabled:
          requirementDefinition.type === RequirementType.SIGNATURE &&
          !!requirementDefinition.dataSourceId
      }
    );
  const sharedFileSettingsQuery =
    queriesSharedFileRequestConfigurations.getSharedFileRequestConfiguration.useQuery(
      {
        sharedFileRequestConfigurationId:
          requirementDefinition.dataSourceId as string
      },
      {
        enabled:
          requirementDefinition.type === RequirementType.DOCUMENT_UPLOAD &&
          !!requirementDefinition.dataSourceId
      }
    );

  const backgroundChecksSettingsQuery =
    queriesBackgroundCheckConfigurations.getBackgroundCheckConfiguration.useQuery(
      {
        backgroundCheckConfigurationId:
          requirementDefinition.dataSourceId as string
      },
      {
        enabled:
          requirementDefinition.type === RequirementType.BACKGROUND_CHECK &&
          !!requirementDefinition.dataSourceId
      }
    );

  // v1 embedded the whole package on the settings response; v3 carries only
  // `packageId`, so the reports list needs the package fetched separately.
  const backgroundCheckPackageQuery =
    queriesBackgroundCheckPackages.getBackgroundCheckPackage.useQuery(
      {
        backgroundCheckPackageId: backgroundChecksSettingsQuery.data
          ?.packageId as string
      },
      { enabled: !!backgroundChecksSettingsQuery.data?.packageId }
    );

  const monitorSettingsQuery =
    queriesInsuranceMonitorConfigurations.getInsuranceMonitorConfiguration.useQuery(
      {
        insuranceMonitorConfigurationId:
          requirementDefinition.dataSourceId as string
      },
      {
        enabled:
          requirementDefinition.type === RequirementType.INSURANCE_COVERAGE &&
          !!requirementDefinition.dataSourceId
      }
    );

  return (
    <WSQueries queries={{ queryEngagementsList }}>
      {() => {
        const loadingByType: Partial<Record<RequirementType, boolean>> = {
          [RequirementType.SIGNATURE]:
            signedDocumentTemplateQuery.isInitialLoading,
          [RequirementType.DOCUMENT_UPLOAD]:
            sharedFileSettingsQuery.isInitialLoading,
          [RequirementType.BACKGROUND_CHECK]:
            backgroundChecksSettingsQuery.isInitialLoading ||
            backgroundCheckPackageQuery.isInitialLoading,
          [RequirementType.BIOMETRIC_IDENTITY_VERIFICATION]: false,
          [RequirementType.INSURANCE_COVERAGE]:
            monitorSettingsQuery.isInitialLoading,
          [RequirementType.REGISTRATION]: false,
          [RequirementType.TAX_VERIFICATION]: false,
          [RequirementType.PAYOUT_METHOD]: false
        };

        if (loadingByType[requirementDefinition.type!]) {
          return <WSLoader.Spinner mt="3XL" size="M" />;
        }

        // The planned-validation checks come off the configuration's own
        // `jobs` — the reusable template's list, so they preview before any
        // file was uploaded (a *request*'s jobs only materialise after one).
        const validationJobs =
          sharedFileSettingsQuery.data?.jobs?.filter(
            job => job.type === SharedFileRequestJobType.VALIDATION
          ) || [];

        const aiSupportByType: Partial<Record<RequirementType, boolean>> = {
          [RequirementType.SIGNATURE]: false,
          [RequirementType.DOCUMENT_UPLOAD]: selectorIsSharedFileNotNoneContext(
            sharedFileSettingsQuery?.data?.contextType
          ),
          [RequirementType.BACKGROUND_CHECK]: false,
          [RequirementType.BIOMETRIC_IDENTITY_VERIFICATION]: true,
          [RequirementType.INSURANCE_COVERAGE]: true,
          [RequirementType.REGISTRATION]: false,
          [RequirementType.TAX_VERIFICATION]: false,
          [RequirementType.PAYOUT_METHOD]: false
        };

        const widgetsByType: Partial<Record<RequirementType, React.ReactNode>> =
          {
            [RequirementType.SIGNATURE]: null,
            [RequirementType.DOCUMENT_UPLOAD]:
              selectorIsSharedFileNotNoneContext(
                sharedFileSettingsQuery?.data?.contextType
              ) ? (
                <>
                  {/* an empty list would render a titled but empty
                  "Verifications" panel */}
                  {validationJobs.length > 0 ? (
                    <AIValidationWidget
                      contextType={RequirementContextType.Payer}
                      validationItems={validationJobs}
                    />
                  ) : null}
                  <WayfinderWidget />
                </>
              ) : null,
            [RequirementType.BACKGROUND_CHECK]: backgroundCheckPackageQuery.data
              ?.searches ? (
              <ReportsInfoWidgetContent
                searches={backgroundCheckPackageQuery.data?.searches}
              />
            ) : null,
            [RequirementType.BIOMETRIC_IDENTITY_VERIFICATION]: (
              <>
                <AIValidationWidget
                  contextType={RequirementContextType.Payer}
                  validationItems={IDENTITY_VERIFICATION_CHECKS}
                />
                <WayfinderWidget />
              </>
            ),
            [RequirementType.INSURANCE_COVERAGE]: (
              <>
                {/*
                // TODO: change to specific validations
                <AIValidationWidget
                  contextType={RequirementContextType.Payer}
                  validationItems={IDENTITY_VERIFICATION_CHECKS}
                />
                */}
                <WayfinderWidget />
              </>
            ),
            [RequirementType.REGISTRATION]: null,
            [RequirementType.TAX_VERIFICATION]: null,
            [RequirementType.PAYOUT_METHOD]: null
          };

        const isDefaultRequirementActive =
          props.requirementDefinition.version === requirementDefinition.version;

        return (
          <WSElement>
            <WSPageToolbar
              actions={
                requirementDefinitionDefaultActions.getOnPreview(
                  requirementDefinition
                )
                  ? [
                      {
                        buttonKind: "Secondary",
                        icon: "view",
                        label: "Preview requirement",
                        onClick:
                          requirementDefinitionDefaultActions.getOnPreview(
                            requirementDefinition
                          )
                      }
                    ]
                  : undefined
              }
              breadcrumbs={[
                {
                  label: "Requirements",
                  onClick: () => {
                    goToRequirementDefinitions(requirementDefinition.status);
                  }
                },
                {
                  label: requirementDefinition.name!
                }
              ]}
              fetchActions={async () =>
                requirementDefinitionDefaultActions
                  .getMenuActions({
                    engagements: queryEngagementsList.flatData ?? [],
                    onDelete: requirementDefinition => {
                      goToRequirementDefinitions(requirementDefinition.status);
                    },
                    requirementDefinition: requirementDefinition
                  })
                  .then(actions =>
                    actions.filter(action => action.name !== "preview")
                  )
              }
              title={requirementDefinition.name!}
              titleAfter={
                <>
                  <SelectRequirementDefinitionVersion
                    requirementDefinition={props.requirementDefinition}
                    value={requirementDefinition.version!}
                    onChange={version => {
                      if (version === props.requirementDefinition.version) {
                        setRequirementDefinition(props.requirementDefinition);
                      } else {
                        const historyItem =
                          props.requirementDefinition.versionHistory?.find(
                            v => v.version === version
                          );

                        if (historyItem) {
                          setRequirementDefinition({
                            ...props.requirementDefinition,
                            ...historyItem,
                            versionScope:
                              props.requirementDefinition.versionScope,
                            versionNote: historyItem.versionNote
                          });
                        }
                      }
                    }}
                  />
                  {requirementDefinition.status ===
                  RequirementDefinitionStatus.INACTIVE ? (
                    <WSPill icon="archive" text="Archived" theme="neutral" />
                  ) : (props.requirementDefinition.versionHistory?.length ??
                      0) > 0 ? (
                    isDefaultRequirementActive ? (
                      <WSPill icon="check" text="Latest" theme="success" />
                    ) : null
                  ) : (
                    <WSPill icon="check" text="Ready" theme="success" />
                  )}

                  {aiSupportByType[requirementDefinition.type!] ? (
                    <WSAvatar
                      colorBackground="garnet"
                      format="square"
                      icon="compass"
                      size="L"
                      type="icon"
                    />
                  ) : null}
                  <WSText.ParagraphXs color="gray500">
                    Last modified{" "}
                    {
                      toWSDate(requirementDefinition.events?.updatedAt)
                        .formattedValue
                    }
                  </WSText.ParagraphXs>
                </>
              }
            />

            <WSGrid>
              <WSGrid.Item span={{ l: "8", s: "12" }}>
                <WSList gap="XL">
                  <WSElement>
                    <WSSectionToolbar title="Requirement information" />
                    <WSPanel mt="M">
                      <RequirementInformation
                        backgroundCheckPackage={
                          backgroundCheckPackageQuery?.data
                        }
                        backgroundChecksSettings={
                          backgroundChecksSettingsQuery?.data
                        }
                        monitorSettings={monitorSettingsQuery?.data}
                        requirementDefinition={requirementDefinition}
                        sharedFileSettings={sharedFileSettingsQuery?.data}
                        singedDocumentTemplate={
                          signedDocumentTemplateQuery?.data
                        }
                      />
                    </WSPanel>
                  </WSElement>

                  {isDefaultRequirementActive && (
                    <WSElement>
                      <WSSectionToolbar
                        mb="M"
                        title="Requirement in engagements"
                      />
                      <EngagementsTable requirementId={requirementId} />
                    </WSElement>
                  )}
                </WSList>
              </WSGrid.Item>
              <WSGrid.Item span={{ l: "4", s: "12" }}>
                <WSList gap="L">
                  <VersionsWidget
                    requirementDefinition={props.requirementDefinition}
                    viewingVersion={requirementDefinition.version!}
                  />

                  <ApprovalWidget
                    reviewStrategy={requirementDefinition.reviewPolicy}
                  />

                  {widgetsByType[requirementDefinition.type!]}

                  {isDefaultRequirementActive && (
                    <RequirementDefinitionHistoryWidget
                      requirementDefinitionId={requirementId}
                    />
                  )}
                </WSList>
              </WSGrid.Item>
            </WSGrid>
          </WSElement>
        );
      }}
    </WSQueries>
  );
};
